import React, { useState, useRef, useEffect, useCallback, Suspense } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Canvas } from '@react-three/fiber';
import { Stage, OrbitControls } from '@react-three/drei';
import { Box, IconButton, Tooltip, Button, Typography, Stack } from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSelector } from 'react-redux';
import { digitalTwinApi } from '../../Restclient/datacollection';
import DTDashboardConfigurator from './dashboard/DTDashboardConfigurator';

import Model from './components/Model';
import CameraController from './components/CameraController';
import LoadingIndicator from './components/LoadingIndicator';
import BusyOverlay from './components/BusyOverlay';
import ComponentBindingPanel from './components/ComponentBindingPanel';
import ZoomControls from './components/ZoomControls';
import ViewModeSelector from './components/ViewModeSelector';
import CanvasErrorBoundary from './components/CanvasErrorBoundary';
import ModelBrowser from './components/ModelBrowser';

import { useModelFiles } from './hooks/useModelFiles';
import { useComponentBindings } from './hooks/useComponentBindings';

function ThreeDScene() {
  const cameraRef = useRef();
  const theme = useSelector((state) => state.themedata.theme);

  const {
    models,
    loading: modelsLoading,
    deleting: modelDeleting,
    uploading: modelUploading,
    selectedModelId,
    selectedModel,
    modelUrl,
    selectModel,
    uploadModel,
    deleteModel,
  } = useModelFiles();

  // Use selectedModelId directly so binding fetch starts immediately on page
  // reload (before the models array has finished loading and selectedModel is set).
  const { bindings, dashboardIndex, getBinding, saveBinding, batchAssignDashboard, removeBinding, refreshBindings } = useComponentBindings(
    selectedModelId || null,
    selectedModel?.displayName || selectedModel?.filename || null
  );

  // Always-current refs so async callbacks and memoized handlers never read stale state.
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;
  const dashboardIndexRef = useRef(dashboardIndex);
  dashboardIndexRef.current = dashboardIndex;

  const [selectedComponent, setSelectedComponent] = useState(null);
  // Array of component IDs selected in edit mode (supports multi-select)
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [viewMode, setViewMode] = useState('color');
  const [appMode, setAppMode] = useState('view'); // 'view' | 'edit'
  const [showModelBrowser, setShowModelBrowser] = useState(false);
  const [showBindingPanel, setShowBindingPanel] = useState(false);
  const [activeDashboardId, setActiveDashboardId] = useState(null);
  const [dashboardEditMode, setDashboardEditMode] = useState(false);
  const [bindingSaving, setBindingSaving] = useState(false);
  const [bindingError, setBindingError] = useState(null);

  // Switching between View and Edit always resets all selection and panel state
  const handleSetAppMode = useCallback((mode) => {
    setAppMode(mode);
    setSelectedComponents([]);
    setSelectedComponent(null);
    setShowBindingPanel(false);
    setActiveDashboardId(null);
    setDashboardEditMode(false);
    setBindingError(null);
  }, []);

  // Click: toggle component in selection (edit) or open dashboard group (view).
  // activeDashboardId is in deps so the "click to close" check is never stale.
  const handleSelectComponent = useCallback(
    (component) => {
      if (appMode === 'view') {
        const raw = bindingsRef.current[component];
        const dashboardId = raw?.dashboard_id || null;

        // Unbound component — not interactive in view mode
        if (!dashboardId) return;

        // Clicking any member of the currently-open group closes/deselects it
        if (activeDashboardId === dashboardId) {
          setActiveDashboardId(null);
          setSelectedComponents([]);
          setSelectedComponent(null);
          return;
        }

        // Auto-select ALL components that share this dashboard — O(1) index lookup.
        const group = dashboardIndexRef.current[dashboardId] || [];

        setSelectedComponent(component);
        setSelectedComponents(group);
        setShowBindingPanel(false);
        setActiveDashboardId(dashboardId);
        return;
      }

      setSelectedComponent(component);
      if (appMode === 'edit') {
        setSelectedComponents((prev) => {
          const next = prev.includes(component)
            ? prev.filter((c) => c !== component)   // deselect on second tap
            : [...prev, component];                  // select on first tap
          if (next.length === 0) setShowBindingPanel(false);
          else { setShowBindingPanel(true); setBindingError(null); }
          return next;
        });
        setActiveDashboardId(null);
      }
    },
    [appMode, activeDashboardId]
  );

  // Kept for programmatic removal (e.g. chip × button in the panel)
  const handleDeselectComponent = useCallback(
    (component) => {
      setSelectedComponents((prev) => {
        const next = prev.filter((c) => c !== component);
        if (next.length === 0) setShowBindingPanel(false);
        return next;
      });
    },
    []
  );

  // Save binding for a single component
  const handleSaveBinding = useCallback(
    async (componentId, label) => {
      try {
        setBindingSaving(true);
        setBindingError(null);
        await saveBinding(componentId, label);
      } catch (err) {
        setBindingError(err.message || 'Failed to save binding');
      } finally {
        setBindingSaving(false);
      }
    },
    [saveBinding]
  );

  // Save bindings for ALL currently selected components (uses componentId as label if no existing label)
  const handleSaveAllBindings = useCallback(async () => {
    try {
      setBindingSaving(true);
      setBindingError(null);
      for (const componentId of selectedComponents) {
        const existing = getBinding(componentId);
        await saveBinding(componentId, existing?.label || componentId);
      }
    } catch (err) {
      setBindingError(err.message || 'Failed to save bindings');
    } finally {
      setBindingSaving(false);
    }
  }, [selectedComponents, getBinding, saveBinding]);

  // Create one dashboard and assign it to ALL currently selected components.
  // Uses batchAssignDashboard (single atomic write) so the local fileDoc state
  // is updated from the actual replace response — no stale SQL re-fetch needed.
  const handleAssignDashboard = useCallback(
    async (dashboardConfig) => {
      try {
        const fileId = selectedModelId || 'default';
        const fileName = selectedModel?.displayName || selectedModel?.filename;
        const components = selectedComponents.length > 0 ? selectedComponents : [selectedComponent];

        // Ensure every selected component has a binding entry (reads from ref, not stale closure)
        for (const componentId of components) {
          const existing = bindingsRef.current[componentId];
          if (!existing) {
            await digitalTwinApi.setComponentBinding(fileId, fileName, componentId, {
              label: componentId,
              dashboard_id: null,
            });
          }
        }

        // Create the shared dashboard
        const res = await digitalTwinApi.createDashboard({
          name: dashboardConfig?.name || `Dashboard - ${components[0]}`,
          tabs: dashboardConfig?.tabs || [{ id: uuidv4(), label: 'Overview', widgets: [] }],
        });

        // Single atomic write: sets dashboard_id for all components at once.
        // batchAssignDashboard also calls setFileDoc with the replace response,
        // so getBinding() is immediately fresh — no SQL re-fetch race condition.
        await batchAssignDashboard(components, res.data.id);

        setActiveDashboardId(res.data.id);
        setShowBindingPanel(false);
        setSelectedComponents([]);
      } catch (err) {
        const detail = err.response?.data?.detail || err.response?.data?.error || err.message;
        setBindingError(`Failed to create dashboard: ${detail}`);
      }
    },
    [selectedComponents, selectedComponent, selectedModelId, selectedModel, batchAssignDashboard]
  );

  const handleOpenDashboard = useCallback((dashboardId) => {
    setActiveDashboardId(dashboardId);
    setShowBindingPanel(false);
  }, []);

  // Switching render modes (color / wireframe / translucent) clears all selection state
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    setSelectedComponents([]);
    setSelectedComponent(null);
    setActiveDashboardId(null);
    setDashboardEditMode(false);
  }, []);

  const handleZoom = useCallback((direction) => {
    if (!cameraRef.current) return;
    const factor = direction === 'in' ? 0.9 : 1.1;
    cameraRef.current.position.multiplyScalar(factor);
    cameraRef.current.updateProjectionMatrix();
  }, []);

  const canvasBackgroundColor = theme === 'dark' ? '#101010' : '#f0f0f0';
  const isDark = theme === 'dark';

  // currentBinding: used for view-mode dashboard display; for edit mode with multi-select,
  // the panel works with selectedComponents array directly.
  const currentBinding = selectedComponent ? getBinding(selectedComponent) : null;

  return (
    <Box
      sx={{
        width: '100%',
        height: '99%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: canvasBackgroundColor,
      }}
    >
      <LoadingIndicator />

      {/* Mode toggle toolbar */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 5,
          display: 'flex',
          gap: 1,
          backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.85)',
          borderRadius: 2,
          padding: '4px 8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}
      >
        <Tooltip title="View Mode">
          <Button
            size="small"
            variant={appMode === 'view' ? 'contained' : 'text'}
            startIcon={<VisibilityIcon />}
            onClick={() => handleSetAppMode('view')}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            View
          </Button>
        </Tooltip>
        <Tooltip title="Edit Mode — click to select/deselect components">
          <Button
            size="small"
            variant={appMode === 'edit' ? 'contained' : 'text'}
            startIcon={<EditIcon />}
            onClick={() => handleSetAppMode('edit')}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Edit
            {appMode === 'edit' && selectedComponents.length > 0 && (
              <Typography
                component="span"
                sx={{
                  ml: 0.75,
                  fontSize: 10,
                  fontWeight: 700,
                  background: '#008CD7',
                  color: '#fff',
                  borderRadius: '10px',
                  px: 0.75,
                  py: 0.1,
                }}
              >
                {selectedComponents.length}
              </Typography>
            )}
          </Button>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, position: 'relative', display: 'flex' }}>
        {showModelBrowser && (
          <ModelBrowser
            models={models}
            loading={modelsLoading}
            uploading={modelUploading}
            selectedModelId={selectedModelId}
            onSelect={(id) => {
              selectModel(id);
              setShowModelBrowser(false);
              setSelectedComponent(null);
              setSelectedComponents([]);
              setActiveDashboardId(null);
            }}
            onUpload={uploadModel}
            onDelete={deleteModel}
            onClose={() => setShowModelBrowser(false)}
          />
        )}

        {/* Edit mode: binding panel overlay */}
        {showBindingPanel && appMode === 'edit' && selectedComponents.length > 0 && (
          <ComponentBindingPanel
            componentIds={selectedComponents}
            fileId={selectedModelId}
            selectedModelName={selectedModel?.displayName || selectedModel?.filename || null}
            getBinding={getBinding}
            onSave={handleSaveBinding}
            onSaveAll={handleSaveAllBindings}
            onAssignDashboard={handleAssignDashboard}
            onRemove={removeBinding}
            onDeselect={handleDeselectComponent}
            onOpenDashboard={handleOpenDashboard}
            onClose={() => { setShowBindingPanel(false); setSelectedComponents([]); }}
            saving={bindingSaving}
            error={bindingError}
          />
        )}

        {modelUrl ? (
          <CanvasErrorBoundary>
            <Canvas
              shadows={false}
              camera={{ fov: 45, position: [0, 0, 5] }}
              onCreated={({ camera }) => (cameraRef.current = camera)}
              style={{ width: '100%', height: '100%' }}
            >
              <color attach="background" args={[canvasBackgroundColor]} />
              <Suspense fallback={null}>
                <Stage intensity={0.08} environment={null}>
                  <Model
                    key={modelUrl}
                    modelUrl={modelUrl}
                    onSelect={handleSelectComponent}
                    viewMode={viewMode}
                    selectedComponent={selectedComponent}
                    editSelectedComponents={selectedComponents}
                    editMode={appMode === 'edit'}
                    appMode={appMode}
                    viewBindings={bindings}
                  />
                </Stage>
              </Suspense>
              <CameraController focusTarget={selectedComponent} />
              <OrbitControls
                enablePan={true}
                enableZoom={true}
                enableRotate={true}
                target={[0, 0, 0]}
                enableDamping={true}
                dampingFactor={0.2}
                rotateSpeed={0.5}
              />
            </Canvas>
          </CanvasErrorBoundary>
        ) : !modelsLoading && (
          /* Empty state — shown when no model is selected and models have loaded */
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{ flex: 1, opacity: 0.45, userSelect: 'none', pointerEvents: 'none' }}
          >
            <ViewInArIcon sx={{ fontSize: 64, color: isDark ? '#fff' : '#555' }} />
            <Typography variant="body1" sx={{ color: isDark ? '#ccc' : '#555', fontWeight: 500 }}>
              No model loaded
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#888' : '#999' }}>
              Open the model browser to select or upload a 3D model
            </Typography>
          </Stack>
        )}

        <ZoomControls onZoom={handleZoom} />

        <Tooltip title="Open Model Browser">
          <IconButton
            onClick={() => setShowModelBrowser((prev) => !prev)}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 4,
              backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
              },
            }}
          >
            <FolderOpenIcon sx={{ color: isDark ? '#fff' : '#333' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <ViewModeSelector viewMode={viewMode} onViewModeChange={handleViewModeChange} />

      {/* Scene-level blocking overlay for initial load and model delete */}
      <BusyOverlay
        open={modelsLoading || modelDeleting}
        message={modelDeleting ? 'Deleting model…' : 'Loading models…'}
      />

      {/* DT Dashboard panel — left-side, expands when dashboard is in edit mode */}
      {activeDashboardId && (
        <Box
          sx={{
            position: 'absolute',
            marginTop: '5px',
            left: 0,
            width: dashboardEditMode ? '65%' : '30%',
            height: '98%',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'width 0.25s ease',
          }}
        >
          <DTDashboardConfigurator
            dashboardId={activeDashboardId}
            componentLabel={currentBinding?.label || selectedComponent}
            onClose={() => {
              setActiveDashboardId(null);
              setDashboardEditMode(false);
              setSelectedComponents([]);
              setSelectedComponent(null);
            }}
            onEditModeChange={setDashboardEditMode}
          />
        </Box>
      )}
    </Box>
  );
}

export default ThreeDScene;
